/**
 * @copyright 2025 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useMemo, useState, useEffect } from 'react'

import {
  DndContext,
  closestCenter,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers'
import { SortableContext, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { FaPlus, FaEdit, FaUserShield, FaPallet } from 'react-icons/fa'
import { FiBell, FiFileText, FiInfo } from 'react-icons/fi'
import { HiOutlineTruck } from 'react-icons/hi'
import { MdSensors } from 'react-icons/md'
// import { RiMapPinFill } from 'react-icons/ri'
import { RiDraggable } from 'react-icons/ri'
import { TbExternalLink, TbSnowflake, TbTemperatureMinus } from 'react-icons/tb'
import { NavLink, useMatch, useNavigate } from 'react-router-dom'

import { Button, TruncateText } from '@/components/ui'
import { useAuth, useDashboard } from '@/hooks'
import useLocalStorage from '@/hooks/useLocalStorage'
import type { DashboardType } from '@/types/dashboard-types'
import { canOpenFresh, redirectToLegacyFresh } from '@/utils/legacyFresh'

import { SortableItem } from './drag-and-drop/SortableItem'
import { ExternalNavItem, NavItem, NavSection } from './NavSection'

import { DashboardIcon } from '../Icons'

interface DashboardNavigationSidebarProps {
  /** The pinned sidebar is down to icons. */
  isIconOnly: boolean
}

type SectionId = 'applications' | 'dashboards' | 'actions'

export interface DashboardNavItem {
  slug: string
  label: string
  icon: React.ReactNode
  description?: string
  type: 'dashboard' | 'action'
  isDefault?: boolean
}
const TrackerIcon = () => (
  <div className="relative">
    <FaPallet />
    <MdSensors size={12} className="absolute -top-1.5 -right-1 rounded-full bg-base-100/20" />
  </div>
)
const DashboardNavigationSidebar: React.FC<DashboardNavigationSidebarProps> = ({ isIconOnly }) => {
  const { hasPermission, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const dashboardContext = useDashboard()
  const isCollapsed = isIconOnly
  // Kept in this browser only, like the width.
  const [collapsedSections, setCollapsedSections] = useLocalStorage<Partial<Record<SectionId, boolean>>>(
    'dashboardNavSectionsCollapsed',
    {}
  )
  const sectionProps = (id: SectionId) => ({
    id,
    collapsed: collapsedSections[id] ?? false,
    onToggle: () => setCollapsedSections((prev) => ({ ...prev, [id]: !(prev[id] ?? false) })),
    isIconOnly: isCollapsed,
  })
  const [dashboards, setDashboards] = useState<DashboardType[]>(dashboardContext?.configuration?.dashboards)

  const [activeItem, setActiveItem] = useState<DashboardNavItem | null>(null)
  const [isEditMode, setIsEditMode] = useState(false)

  const isAlertAcknowledgementRoute = useMatch('/alert-acknowledgement/*')

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  useEffect(() => {
    const d = dashboardContext?.configuration?.dashboards
    if (d) {
      setDashboards(d)
    }
  }, [dashboardContext?.configuration?.dashboards])

  const { defaultDashboards, nonDefaultDashboards, actionItems } = useMemo(() => {
    const defaultDashboards = dashboards
      .filter((panel) => panel.isDefault)
      .map((panel) => ({
        slug: panel.slug,
        label: panel.title,
        icon: <DashboardIcon name={panel.icon ?? 'dashboard'} color={panel.iconColor} />,
        description: panel.description,
        type: 'dashboard' as const,
        isDefault: panel.isDefault,
      }))

    const nonDefaultDashboards = dashboards
      .filter((panel) => !panel.isDefault)
      .map((panel) => ({
        slug: panel.slug,
        label: panel.title,
        icon: <DashboardIcon name={panel.icon ?? 'chartNoAxes'} color={panel.iconColor} />,
        description: panel.description,
        type: 'dashboard' as const,
        isDefault: panel.isDefault,
      }))

    const actionItems = [
      {
        slug: 'create-new',
        label: 'Create Dashboard',
        icon: <FaPlus />,
        description: 'Create a new dashboard layout',
        type: 'action' as const,
      },
    ]

    // Cooltrax staff only. The admin endpoints enforce this server-side as well, so
    // hiding the link is convenience rather than the actual control.
    if (hasPermission('staff')) {
      actionItems.push({
        slug: 'admin',
        label: 'Admin',
        icon: <FaUserShield />,
        description: 'Manage users and integrations',
        type: 'action' as const,
      })
    }
    /*
    if (user && /cooltrax.com/i.test(user.email)) {
      actionItems.push({
        slug: 'asset-map',
        label: 'Back to Asset Map',
        icon: <RiMapPinFill />,
        description: 'View the Asset Map',
        type: 'action' as const,
      })
    }
    */
    return { defaultDashboards, nonDefaultDashboards, actionItems }
  }, [dashboards /*, user?.email*/])

  const handleDragStart = (e: DragStartEvent): void => {
    const item = nonDefaultDashboards.find((item) => item.slug === e.active.id)
    setActiveItem(item || null)
  }

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event

    if (active.id !== over?.id && over?.id) {
      const oldIndex = nonDefaultDashboards.findIndex((item) => item.slug === active.id)
      const newIndex = nonDefaultDashboards.findIndex((item) => item.slug === over.id)

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedNonDefault = arrayMove(nonDefaultDashboards, oldIndex, newIndex)
        const originalDefaultDashboards = dashboards.filter((item) => item.isDefault)
        const newOrder = [
          ...originalDefaultDashboards,
          ...reorderedNonDefault.map((item) => dashboards.find((d) => d.slug === item.slug)!),
        ]

        setDashboards(newOrder)
        dashboardContext.reorderDashboards(newOrder)
      }
    }
    setTimeout(() => {
      setActiveItem(null)
    }, 100)
  }

  const handleNavClick = (item: DashboardNavItem): void => {
    if (item.type === 'dashboard') {
      navigate(`/dashboards/${item.slug}`)
    } else if (item.slug === 'create-new') {
      navigate('/dashboards/new')
    } else if (item.slug === 'settings') {
      navigate('/dashboards/settings')
    } else if (item.slug === 'asset-map') {
      navigate('/assets')
    } else if (item.slug === 'admin') {
      navigate('/admin')
    }
  }

  return (
    <nav className="p-2">
      <NavSection {...sectionProps('applications')} label="Applications" first>
        {/* MEGA-779: gated on the assetMap grant again (MEGA-707 had opened it to everyone). */}
        {hasPermission('assetMap') && (
          <NavItem
            to="/assets"
            icon={<HiOutlineTruck />}
            label="Assets"
            description="View asset map and details"
            isIconOnly={isCollapsed}
          />
        )}
        {(hasPermission('preview') || hasPermission('trackerMap')) && (
          <NavItem
            to="/trackers"
            end
            icon={<TrackerIcon />}
            label="Trackers"
            description="View tracker map and details"
            isIconOnly={isCollapsed}
          />
        )}
        {hasPermission('coldStorage') && (
          <NavItem
            to="/cold-storage"
            end
            icon={<TbSnowflake />}
            label="Cold Storage"
            description="Fridge/freezer monitoring"
            isIconOnly={isCollapsed}
          />
        )}
        {hasPermission('coldStorage') && (
          <NavItem
            to="/blast-freezer"
            end
            icon={<TbTemperatureMinus />}
            label="Blast Freezer"
            description="Freezer pull-down timing"
            isIconOnly={isCollapsed}
          />
        )}
        {hasPermission('auditLogs') && (
          <NavItem
            to="/audit-logs"
            icon={<FiFileText />}
            label="Audit Logs"
            description="Track system changes"
            isIconOnly={isCollapsed}
          />
        )}
        {isAlertAcknowledgementRoute && (
          <div
            className="group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium bg-primary text-primary-content"
            title={isCollapsed ? 'Alert Acknowledgement' : undefined}
          >
            <div className="flex items-center gap-3 flex-1">
              <span className="shrink-0 w-5 h-5 flex items-center justify-center">
                <FiBell />
              </span>
              {!isCollapsed && (
                <div className="flex-1 min-w-0">
                  <div className="truncate">Alert Acknowledgement</div>
                  <div className="text-xs opacity-75 truncate">Manage alert actions</div>
                </div>
              )}
            </div>
          </div>
        )}
        {canOpenFresh(isAuthenticated) && (
          <ExternalNavItem
            icon={<TbExternalLink />}
            label="Fresh"
            description="Open the Fresh app"
            isIconOnly={isCollapsed}
            onClick={redirectToLegacyFresh}
          />
        )}
      </NavSection>

      <NavSection
        {...sectionProps('dashboards')}
        label="Dashboards"
        trailing={
          <Button variation="ghost" size="xs" handleClick={() => setIsEditMode(!isEditMode)}>
            <FaEdit size={14} />
          </Button>
        }
      >
        {isEditMode && (
          <div className="text-xs mb-4 bg-blue-300 border border-blue-400 rounded-md flex items-start justify-between">
            <FiInfo size={24} />
            <p className="pl-2">Edit Mode: Here you can re-arrange the order of your dashboards</p>
          </div>
        )}
        {defaultDashboards.map((item) => (
          <NavItem
            key={item.slug}
            to={`/dashboards/${item.slug}`}
            icon={item.icon}
            label={item.label}
            description={item.description}
            isIconOnly={isCollapsed}
          />
        ))}

        <div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            onDragStart={handleDragStart}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          >
            <SortableContext
              items={nonDefaultDashboards.map((item) => item.slug)}
              strategy={verticalListSortingStrategy}
            >
              {nonDefaultDashboards.map((item) => (
                <SortableItem key={item.slug} isPinned={false} id={item.slug}>
                  {({ dragHandleProps, setActivatorNodeRef, isDragging }) => (
                    <NavLink
                      ref={isEditMode ? setActivatorNodeRef : undefined}
                      {...(isEditMode ? dragHandleProps : {})}
                      style={{ pointerEvents: isDragging ? 'none' : 'auto' }}
                      key={item.slug}
                      to={`/dashboards/${item.slug}`}
                      onClick={(e) => {
                        if (isDragging) {
                          e.preventDefault()
                          e.stopPropagation()
                          return false
                        }
                      }}
                    >
                      {({ isActive }) => (
                        <div
                          className={`group flex items-center gap-3 pl-3 py-2 rounded-lg text-sm font-medium transition-colors  ${
                            isActive
                              ? 'bg-primary text-primary-content'
                              : 'text-base-content/70 hover:bg-base-300/30 hover:text-base-content'
                          } ${isEditMode ? 'border border-dashed border-black cursor-grab' : 'border-none'}`}
                          title={isCollapsed ? item.label : undefined}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <span className="shrink-0 w-5 h-5 flex items-center justify-center">{item.icon}</span>
                            {!isCollapsed && (
                              <div className="flex-1 min-w-0 flex  flex-col ">
                                <TruncateText
                                  wrapperType="truncate"
                                  tooltipText={item.label}
                                  renderItem={() => <>{item.label}</>}
                                />

                                {item.description && (
                                  <div className="text-xs opacity-75 truncate">{item.description}</div>
                                )}
                              </div>
                            )}
                          </div>

                          <div
                            className={` shrink-0 w-5 h-5 items-center justify-center ${isCollapsed ? 'hidden' : 'flex'}`}
                          >
                            <RiDraggable
                              size={18}
                              className={`rounded-r-lg transition-opacity ${
                                isActive ? 'text-primary-content' : 'text-base-content'
                              } ${isEditMode ? 'block' : 'hidden'}`}
                            />
                          </div>
                        </div>
                      )}
                    </NavLink>
                  )}
                </SortableItem>
              ))}
            </SortableContext>

            <DragOverlay>
              {activeItem ? (
                <div className="cursor-grabbing" style={{ cursor: 'grabbing' }}>
                  <div
                    className={`group flex items-center gap-3 pl-3 py-2 rounded-lg text-sm font-medium bg-base-300 text-base-content shadow-lg ${isEditMode ? 'border border-dashed border-black cursor-grabbing' : 'border-none'}`}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <span className="shrink-0 w-5 h-5 flex items-center justify-center">{activeItem.icon}</span>
                      {!isCollapsed && (
                        <div className="flex-1 min-w-0">
                          <span>{activeItem.label}</span>

                          {activeItem.description && (
                            <div className="text-xs opacity-75 truncate">{activeItem.description}</div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="shrink-0 w-5 h-5 flex items-center justify-center">
                      <RiDraggable size={18} className="text-base-content" />
                    </div>
                  </div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      </NavSection>

      <NavSection {...sectionProps('actions')} label="Actions">
        {actionItems.map((item) => (
          <button
            key={item.slug}
            onClick={() => handleNavClick(item)}
            className="cursor-pointer w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-base-content/70 hover:bg-base-300 hover:text-base-content"
            title={isCollapsed ? item.label : undefined}
          >
            <span className="shrink-0 w-5 h-5 flex items-center justify-center">{item.icon}</span>
            {!isCollapsed && (
              <div className="flex-1 min-w-0 text-left">
                <div className="truncate">{item.label}</div>
                {item.description && <div className="text-xs opacity-75 truncate">{item.description}</div>}
              </div>
            )}
          </button>
        ))}
      </NavSection>
    </nav>
  )
}

export default DashboardNavigationSidebar
